import './index.css';
import './styles/uploadWidget.css'; // globalny redesign drop-zone Bytescale (v4 nie ma customCSS)
import '@smastrom/react-rating/style.css'; // globalne style dla <Rating> (inaczej gwiazdki są niewidoczne poza WelcomeComponent)
import './utils/i18n';
import './axios/axionInstance'; // Initialize axios interceptors
import { initFeatureFlags } from './config/featureFlags'; // Flagi z Remote Config
import { initDevTools } from './utils/devTools'; // Initialize dev tools

import { HeroUIProvider } from '@heroui/system';
import { ToastProvider } from '@heroui/toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { FC, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { useIsMobile } from '@/hooks';

import { toBcp47Locale } from '@/utils/locale';

import { theme } from './config/theme';
import { BreadcrumbsProvider } from './contexts/BreadcrumbsContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import reportWebVitals from './reportWebVitals';
import { App } from './views/App';

export type { Theme } from './config/theme';
export { theme };

const queryClient = new QueryClient();

// Wrapper component that uses the theme from context
const ThemedApp: FC<{ children: ReactNode }> = ({ children }) => {
  const { theme: currentTheme } = useThemeMode();
  const isMobile = useIsMobile();
  const { i18n } = useTranslation();

  return (
    <ThemeProvider theme={currentTheme}>
      <HeroUIProvider locale={toBcp47Locale(i18n.language)}>
        <ToastProvider
          toastOffset={isMobile ? 60 : 24}
          maxVisibleToasts={3}
          placement="bottom-center"
          // Toasty muszą stać nad wszystkimi warstwami modali (overlay 1200,
          // potwierdzenia 1300, biblioteka mediów 1260), inaczej błąd chowa się pod modalem.
          regionProps={{ className: 'z-[2000]' }}
        />
        {children}
      </HeroUIProvider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

// Initialize developer tools
initDevTools();

// Flagi funkcji — świadomie BEZ `await`: aplikacja startuje natychmiast z wartościami
// domyślnymi (wszystko wyłączone), a store przerysuje komponenty, gdy odpowiedź dojdzie.
void initFeatureFlags();

root.render(
  <React.StrictMode>
    {/* `v7_startTransition`: nawigacja idzie przez `startTransition`, więc React
        przytrzymuje poprzedni ekran na czas dociągania chunku trasy, zamiast od razu
        gasić go na rzecz fallbacku. */}
    <Router future={{ v7_startTransition: true }}>
      <BreadcrumbsProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeModeProvider>
            <ThemedApp>
              <App />
            </ThemedApp>
          </ThemeModeProvider>
        </QueryClientProvider>
      </BreadcrumbsProvider>
    </Router>
  </React.StrictMode>,
);

reportWebVitals();
